import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, shiftsAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    // Initialise from localStorage immediately so the app renders without waiting for the API
    const [user, setUser] = useState(() => {
        try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
    });
    const [shift, setShift] = useState(() => {
        try { return JSON.parse(localStorage.getItem('shift')); } catch { return null; }
    });
    const [token, setToken] = useState(localStorage.getItem('token'));
    // Only block rendering when we have a token but no cached user yet (first login on device)
    const [loading, setLoading] = useState(!!localStorage.getItem('token') && !localStorage.getItem('user'));

    useEffect(() => {
        if (token) {
            // Verify session in background; does not block render when cache is present
            loadSession();
        } else {
            setLoading(false);
        }
    }, [token]);

    const loadSession = async () => {
        try {
            const response = await authAPI.getSession();
            setUser(response.data.user);
            setShift(response.data.shift);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            localStorage.setItem('shift', JSON.stringify(response.data.shift));
        } catch (error) {
            console.error('Failed to load session:', error);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (credentials) => {
        try {
            const response = await authAPI.login(credentials);
            const { token, user, shift } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            localStorage.setItem('shift', JSON.stringify(shift));
            setToken(token);
            setUser(user);
            setShift(shift);

            return { success: true, data: response.data };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error || 'Login failed',
                requiresApproval: error.response?.data?.requiresApproval,
                existingShift: error.response?.data?.existingShift
            };
        }
    };

    const logout = async (endShift = false) => {
        try {
            if (token) {
                await authAPI.logout(endShift);
            }
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('shift');
            setToken(null);
            setUser(null);
            setShift(null);
        }
    };

    const endShift = async () => {
        try {
            await shiftsAPI.end();
            setShift(null);
            localStorage.removeItem('shift');
            return { success: true };
        } catch (error) {
            return { success: false, error: error.response?.data?.error || 'Failed to end shift' };
        }
    };

    // Stay on for the next shift as OT: the server closes this shift and starts the OT one
    const continueOnOT = async () => {
        try {
            const res = await shiftsAPI.continueOT();
            setShift(res.data.shift);
            localStorage.setItem('shift', JSON.stringify(res.data.shift));
            return { success: true, otMarkedOn: res.data.otMarkedOn };
        } catch (error) {
            return { success: false, error: error.response?.data?.error || 'Could not start the OT shift' };
        }
    };

    const isAdmin = () => {
        return user?.role === 'admin';
    };

    const isWriter = () => {
        return user?.role === 'writer';
    };

    // Writer is in charge of stock and attendance; Admin can always do the same.
    const canManageStock = () => {
        return user?.role === 'admin' || user?.role === 'writer';
    };

    const value = {
        user,
        shift,
        loading,
        isAuthenticated: !!user,
        isAdmin,
        isWriter,
        canManageStock,
        login,
        logout,
        endShift,
        continueOnOT
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
